import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useMatch, useNavigate } from "react-router";
import type { ProjectDTO } from "@roomate/core";
import { useProjects } from "./api.ts";
import { cx } from "./ui.tsx";

/*
 * The current project: one choice, made in the header, that Home, the Office board, Ideas,
 * Activity and quick-add all follow. Inbox, Conversations and Team stay across every project.
 */

export const PROJECT_STORAGE_KEY = "roomate.projectId";

type Store = Pick<Storage, "getItem" | "setItem">;

function browserStore(): Store | null {
  try {
    return localStorage;
  } catch {
    return null; // storage blocked
  }
}

export function readStoredProjectId(store: Store | null = browserStore()): string | null {
  try {
    return store?.getItem(PROJECT_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function storeProjectId(id: string, store: Store | null = browserStore()): void {
  try {
    store?.setItem(PROJECT_STORAGE_KEY, id);
  } catch {
    // storage blocked
  }
}

/** The stored id when it names a real project; otherwise the first project; otherwise nothing. */
export function resolveProjectId(stored: string | null, projects: readonly { id: string }[]): string {
  if (stored && projects.some((p) => p.id === stored)) return stored;
  return projects[0]?.id ?? "";
}

type CurrentProject = {
  /** "" until the project list has loaded (or when there are none). */
  projectId: string;
  /** The project list has settled, so scoped queries may run (with no projects they fetch unscoped). */
  ready: boolean;
  /** More than one project: data is filtered to the current one. */
  scoped: boolean;
  project: ProjectDTO | null;
  projects: ProjectDTO[];
  select: (id: string) => void;
};

const Ctx = createContext<CurrentProject | null>(null);

export function ProjectProvider({ children }: { children: ReactNode }) {
  const query = useProjects();
  const projects = query.data;
  const ready = query.isSuccess || query.isError;
  const list = useMemo(() => projects ?? [], [projects]);
  const [stored, setStored] = useState<string | null>(() => readStoredProjectId());
  const projectId = resolveProjectId(stored, list);

  const navigate = useNavigate();
  const routeId = useMatch("/projects/:id")?.params.id ?? null;

  // Picking another project while looking at a project page moves that page along with it.
  const select = useCallback(
    (id: string) => {
      storeProjectId(id);
      setStored(id);
      if (routeId && routeId !== id) navigate(`/projects/${id}`);
    },
    [routeId, navigate],
  );

  // Opening /projects/:id selects that project (once, when the route changes).
  const routeKnown = !!routeId && list.some((p) => p.id === routeId);
  useEffect(() => {
    if (routeId && routeKnown) select(routeId);
  }, [routeId, routeKnown, select]);

  const value = useMemo<CurrentProject>(
    () => ({ projectId, ready, scoped: list.length > 1, project: list.find((p) => p.id === projectId) ?? null, projects: list, select }),
    [projectId, ready, list, select],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCurrentProject(): CurrentProject {
  const value = useContext(Ctx);
  if (!value) throw new Error("useCurrentProject must be used inside ProjectProvider");
  return value;
}

/** "Roomate [project ▾]" — rendered only when there is something to switch between. */
export function ProjectSwitcher() {
  const { projectId, projects, select } = useCurrentProject();
  if (projects.length < 2) return null;
  return (
    <label className="relative flex items-center">
      <span className="sr-only">Project</span>
      <select
        data-testid="project-switcher"
        value={projectId}
        onChange={(e) => select(e.target.value)}
        className={cx(
          "max-w-[5rem] cursor-pointer sm:max-w-[11rem] appearance-none truncate rounded-md bg-transparent py-1 pl-2 pr-6 text-[13px] text-ink-2 outline-none transition-colors",
          "hover:text-ink focus-visible:ring-1 focus-visible:ring-rule",
        )}
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <span aria-hidden className="pointer-events-none absolute right-2 text-[10px] text-ink-3">
        ▾
      </span>
    </label>
  );
}
